import { Briefcase } from "lucide-react";
import { useContext } from "react";
import { useCookies } from "react-cookie";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import SummaryCard, { formatMonthYear, joinMeta } from "@/app/components/ResumeBuilder/SummaryCard";
import type { ResumeContextValue, WorkHistoryItem } from "@/app/types/resume";

import ResumeContext from "../../../context/ResumeContext";
import { resumeObjectToContextFormat } from "../../../handlers/valueTransformation";
import { StaticValue } from "../../../seed/constants";
import { usePatchResumeSection } from "../../../services/create-resume/queries";
import { RESUME_ACTIONS, RESUME_SECTIONS } from "../../../services/create-resume/resumeSections";

interface SummaryBoxProps {
	item: WorkHistoryItem;
	index: number | string;
}

const SummaryBox = ({ item, index }: SummaryBoxProps) => {
	const navigate = useNavigate();
	const { resumeData, setResumeData } = useContext(ResumeContext) as ResumeContextValue;

	function onSuccess(response: unknown) {
		resumeObjectToContextFormat(response, setResumeData);
	}

	function onError() {
		//TODO: handle error
		toast.error("Something went wrong");
	}
	const [cookies] = useCookies(["resume"]);

	const staticValue = new StaticValue();
	const staticValues = staticValue.getDefaultValues();
	const resumeId = cookies[staticValues.localStorageResumeId];

	const patchSection = usePatchResumeSection();

	const deleteWorkHandler = () => {
		const itemToBeDeleted = item;

		const newActiveIndex = resumeData.activeWorkHistoryIndex === 0 ? 0 : resumeData.activeWorkHistoryIndex - 1;

		const filteredWorkDetails = resumeData.workHistory.filter((workItem) => workItem !== itemToBeDeleted);
		setResumeData((prevState) => {
			return {
				...prevState,
				workHistory: filteredWorkDetails,
				activeWorkHistoryIndex: newActiveIndex,
			};
		});

		patchSection.mutate(
			{
				resumeId,
				section: RESUME_SECTIONS.EXPERIENCE,
				data: { referenceId: item.referenceId },
				action: RESUME_ACTIONS.DELETE,
			} as never,
			{ onSuccess, onError },
		);
	};

	function edit() {
		navigate("/resume/work-history", {
			state: {
				referenceId: index,
			},
		});
	}

	// The location parts were joined with hard-coded commas guarded by a
	// `country !== "undefined"` string test, so a job with no city opened with a
	// comma and one with no region printed two in a row.
	const location = joinMeta([item.city, item.region, item.country]);

	const isCurrent = item.currentEmployer || !item.endDate;
	const startedOn = formatMonthYear(item.startDate);
	const endedOn = isCurrent ? "Present" : formatMonthYear(item.endDate);
	// An en dash range only makes sense with both ends; a half-filled entry shows
	// whichever end it has rather than "Invalid Date – Present".
	const dateRange = startedOn && endedOn ? `${startedOn} – ${endedOn}` : startedOn || endedOn;

	return (
		<SummaryCard
			icon={<Briefcase className="size-5" />}
			title={item.jobTitle}
			badge={isCurrent ? "Current" : undefined}
			badgeTone="accent"
			subtitle={item.employer}
			meta={[location, dateRange]}
			html={item.details}
			onEdit={edit}
			onDelete={deleteWorkHandler}
			deleteTitle="Delete this position?"
			deleteDescription={`"${joinMeta([item.jobTitle, item.employer], " at ") || "This position"}" will be removed from your resume. You cannot undo this.`}
			entryLabel={item.jobTitle}
		/>
	);
};

export default SummaryBox;
